import React from 'react';
import styled, { useTheme } from 'styled-components';
import Big from 'big.js';
import {
  AccountInfoResult,
  ContractResult,
  Deploy,
  FTEntryPointType,
  isContractTypeCep18,
} from '@block-explorer/entities';
import { Trans, useTranslation } from 'react-i18next';

import {
  BodyText,
  formatTimestamp,
  HashLength,
  Tooltip,
} from '@block-explorer/ui';
import {
  Avatar,
  Badge,
  FlexColumn,
  FlexRow,
  formatNumber,
  formatTimestampAge,
  PageTile,
  TruncateBox,
  useMatchMedia,
} from '@make-software/cspr-design';
import { deriveAccountInfo } from '../../utils/account';
import { isValidPublicKey } from 'casper-js-sdk';
import { ACCOUNT_DETAILS_PATH, BLOCK_DETAILS_PATH } from '../../router/paths';
import { buildRouterPath } from '../../router/utils';
import TooltipWithExtendedInfo from '../../components/tooltip-with-extended-info/tooltip-with-extended-info';
import {
  DeployStatus,
  DeployStatusSize,
  HashLink,
  WasmProxyBadge,
} from '../../components';
import { isWASMProxyTransaction } from '../../utils/contract';
import { TransactionHashLink } from '../hash-link/transaction-hash-link';
import { PrecisionCase } from '../../utils/currency';
import CsprAmount from '../cspr-amount/cspr-amount';
import { TableDataAccountInfo } from '../table-data-account-info/table-data-account-info';
import { HistoryLink } from '../history-link/history-link';
import {
  DeployResultRow,
  ResultRowVariation,
} from '../deploy-actions/deploy-result-row';
import {
  ActionIdentificationHashesType,
  DeployActionRow,
} from '../deploy-actions/deploy-action-row';

const StyledPageTile = styled(PageTile)(() => ({
  marginBottom: 8,
  height: 'auto',
}));

const DesktopFeedItemContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    padding: '20px',
    width: '100%',
    '> * + *': {
      width: '100%',
    },
  })
);

const MobileFeedItemContainer = styled(DesktopFeedItemContainer)(({ theme }) =>
  theme.withMedia({
    padding: '16px',
    '> * + *': {
      marginTop: [16, 0],
      paddingTop: 16,
    },
    '> div:not(:first-child)': {
      position: 'relative',
      flexWrap: 'wrap',
    },
    '>div:not(:first-child):after': {
      content: "''",
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      borderBottom: theme.border.tableRowSeparator,
    },
  })
);

const CommonDataContainer = styled(FlexRow)(({ theme }) => ({
  width: '100%',
  justifyContent: 'space-between',
  position: 'relative',
  paddingBottom: 12,
  ':after': {
    content: "''",
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    borderBottom: theme.border.tableRowSeparator,
  },
}));

const WrappedContainer = styled(FlexRow)(({ theme }) => ({
  '> div': {
    flexWrap: 'wrap',
  },
  flexWrap: 'wrap',
}));

const StyledFlexColumn = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    width: '100%',
  })
);

const FacilitatorBadge = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Tooltip
      tooltipContent={t(
        'Facilitator service account that submitted and performed the payment for this transaction. Account that authorized this transaction can be checked on Transaction Details page.'
      )}
    >
      <FlexRow gap={3} align={'center'}>
        <Badge
          lineHeight={'xxs'}
          label={'FACILITATOR'}
          variation={theme.styleguideColors.contentLightBlue}
        />
      </FlexRow>
    </Tooltip>
  );
};

const BlockFeedInfo = ({ deploy }: { deploy: Deploy }) => (
  <FlexRow itemsSpacing={8} align={'center'}>
    <BodyText size={3} noWrap variation={'darkGray'}>
      Block:
    </BodyText>
    <BodyText size={3} monotype>
      {deploy.blockHash == null ? (
        'N/A'
      ) : (
        <HistoryLink
          href={buildRouterPath(
            BLOCK_DETAILS_PATH,
            { id: deploy.blockHash },
            undefined
          )}
          ariaDescription={'Link to block details'}
        >
          {formatNumber(deploy.blockHeight)}
        </HistoryLink>
      )}
    </BodyText>
    <BodyText size={3} noWrap>
      ·
    </BodyText>
    <BodyText size={3} noWrap variation={'darkGray'}>
      {formatTimestampAge(deploy.timestamp)}
    </BodyText>
  </FlexRow>
);

interface ActivityFeedItemProps {
  deploy: Deploy;
  loading: boolean;
  actionIdentificationHashes: ActionIdentificationHashesType;
  getAccountInfo: <T = AccountInfoResult>(
    publicKey: string
  ) => T | null | undefined;
  getContractPackagePath: (hash: string) => string | null;
  getContractInfoByHash?: (
    contractHash: string
  ) => ContractResult | null | undefined;
}

export const ActivityFeedItem = ({
  deploy,
  loading,
  actionIdentificationHashes,
  getAccountInfo,
  ...rest
}: ActivityFeedItemProps) => {
  const {
    callerHash,
    deployHash,
    paymentAmount,
    refundAmount,
    callerCsprName,
    entryPoint,
    contractPackage,
  } = deploy;
  const { t } = useTranslation();

  const callerPublicKey = deploy.callerPublicKey || deploy.callerHash;
  const accountInfo = getAccountInfo<AccountInfoResult>(
    callerPublicKey || callerHash
  );

  const accountInfoDetails = deriveAccountInfo(
    accountInfo?.account_info || accountInfo?.centralized_account_info
  );

  const logo = accountInfoDetails && accountInfoDetails?.logo;
  const name = accountInfoDetails?.name;
  const csprName = accountInfo?.cspr_name || deploy.callerCsprName;

  const keyTooltipCaption = isValidPublicKey(callerPublicKey)
    ? t('Public Key')
    : t('Account hash');

  const path = buildRouterPath(
    ACCOUNT_DETAILS_PATH,
    { id: callerPublicKey },
    undefined
  );
  const chargedAmount = Big(paymentAmount || '0')
    .minus(refundAmount || '0')
    .toString();
  const isCep18 = isContractTypeCep18(contractPackage);
  const isAuthorizedTransfer =
    isCep18 &&
    entryPoint?.name === FTEntryPointType.transfer_with_authorization;

  const onAbove = (
    <DesktopFeedItemContainer itemsSpacing={12}>
      <CommonDataContainer>
        <FlexRow align={'center'}>
          <DeployStatus deployResult={deploy} size={DeployStatusSize.Small} />
          <Tooltip tooltipContent={deployHash} caption={t('Transaction Hash')}>
            <BodyText size={3} scale={'sm'} monotype>
              <TransactionHashLink isDetails hash={deployHash} />
            </BodyText>
          </Tooltip>
        </FlexRow>
        <FlexRow justify={'flex-end'} itemsSpacing={8} align={'baseline'}>
          <Tooltip tooltipContent={formatTimestamp(deploy.timestamp)}>
            <BodyText size={3} noWrap variation={'darkGray'}>
              {formatTimestampAge(deploy.timestamp)}
            </BodyText>
          </Tooltip>
          <BodyText size={3} noWrap>
            ·
          </BodyText>
          <BodyText size={3} noWrap variation={'darkGray'}>
            Block:
          </BodyText>
          <BodyText size={3} monotype>
            {deploy.blockHash == null ? (
              'N/A'
            ) : (
              <HistoryLink
                href={buildRouterPath(
                  BLOCK_DETAILS_PATH,
                  { id: deploy.blockHash },
                  undefined
                )}
                ariaDescription={'Link to block details'}
              >
                {formatNumber(deploy.blockHeight)}
              </HistoryLink>
            )}
          </BodyText>
          <BodyText size={3} noWrap>
            ·
          </BodyText>

          <FlexRow itemsSpacing={4} align={'baseline'}>
            <BodyText size={3} variation={'darkGray'}>
              <Trans t={t}>Charge: </Trans>
            </BodyText>
            <BodyText size={3} monotype noWrap>
              <CsprAmount
                motes={chargedAmount}
                precisionCase={PrecisionCase.deployCost}
              />
            </BodyText>
          </FlexRow>
        </FlexRow>
      </CommonDataContainer>

      <FlexRow itemsSpacing={12}>
        {logo ? (
          <Avatar src={logo} loading={loading} alt={'Account logo'} />
        ) : (
          <Avatar hash={callerPublicKey || callerHash} loading={loading} />
        )}
        <StyledFlexColumn itemsSpacing={12}>
          <FlexRow itemsSpacing={8} align={'center'}>
            <TooltipWithExtendedInfo
              extendedLine={{ title: csprName, caption: t('CSPR.name') }}
              tooltipCaption={keyTooltipCaption}
              hash={callerPublicKey || callerHash}
            >
              <FlexColumn>
                <BodyText size={3} scale="sm" monotype={!csprName}>
                  <HashLink
                    href={path}
                    hash={callerPublicKey}
                    csprName={csprName}
                    hashLength={HashLength.TINY}
                    ariaDescription={`Link to Account page`}
                  />
                </BodyText>
              </FlexColumn>
            </TooltipWithExtendedInfo>
            {name ? (
              <FlexRow>
                <TruncateBox size={5}>
                  <BodyText size={3} variation="darkGray" noWrap>
                    {name}
                  </BodyText>
                </TruncateBox>
              </FlexRow>
            ) : null}
            {isAuthorizedTransfer ? <FacilitatorBadge /> : null}
          </FlexRow>
          <FlexRow>
            <FlexRow itemsSpacing={8}>
              {isWASMProxyTransaction(deploy.executionTypeId) && (
                <WasmProxyBadge />
              )}
              <BodyText size={3} scale={'sm'}>
                <WrappedContainer>
                  <DeployActionRow
                    deploy={deploy}
                    deployRawData={null}
                    loading={loading}
                    actionIdentificationHashes={actionIdentificationHashes}
                    getAccountInfo={getAccountInfo}
                    {...rest}
                  />
                </WrappedContainer>
              </BodyText>
            </FlexRow>
          </FlexRow>
          <DeployResultRow
            deploy={deploy}
            loading={loading}
            actionIdentificationHashes={actionIdentificationHashes}
            variation={ResultRowVariation.gray}
            getAccountInfo={getAccountInfo}
            shouldCollapse
            {...rest}
          />
        </StyledFlexColumn>
      </FlexRow>
    </DesktopFeedItemContainer>
  );

  const onMobile = (
    <MobileFeedItemContainer>
      <FlexRow justify={'space-between'} itemsSpacing={10}>
        <FlexRow align={'center'}>
          <DeployStatus deployResult={deploy} size={DeployStatusSize.Small} />
          <Tooltip tooltipContent={deployHash} caption={t('Transaction Hash')}>
            <BodyText size={3} monotype>
              <TransactionHashLink isDetails hash={deployHash} />
            </BodyText>
          </Tooltip>
        </FlexRow>
        <FlexRow itemsSpacing={4} align={'baseline'} wrap={'wrap'}>
          <BodyText size={3} variation={'darkGray'}>
            <Trans t={t}>Charge: </Trans>
          </BodyText>
          <BodyText size={3} monotype noWrap>
            <CsprAmount
              motes={chargedAmount}
              precisionCase={PrecisionCase.deployCost}
            />
          </BodyText>
        </FlexRow>
      </FlexRow>
      <FlexColumn itemsSpacing={12}>
        <FlexRow itemsSpacing={4} align={'center'} wrap={'wrap'}>
          <TableDataAccountInfo
            logo={logo}
            name={name}
            publicKey={callerPublicKey || callerHash}
            csprName={callerCsprName || csprName}
            loading={loading}
            nameSize={1}
          />
          {isAuthorizedTransfer ? <FacilitatorBadge /> : null}
        </FlexRow>

        <FlexRow itemsSpacing={8}>
          {isWASMProxyTransaction(deploy.executionTypeId) && <WasmProxyBadge />}
          <BodyText size={3} scale={'sm'}>
            <WrappedContainer>
              <DeployActionRow
                deploy={deploy}
                deployRawData={null}
                loading={loading}
                actionIdentificationHashes={actionIdentificationHashes}
                getAccountInfo={getAccountInfo}
                {...rest}
              />
            </WrappedContainer>
          </BodyText>
        </FlexRow>
      </FlexColumn>
      <FlexColumn itemsSpacing={16}>
        <BlockFeedInfo deploy={deploy} />
        <DeployResultRow
          deploy={deploy}
          loading={loading}
          variation={ResultRowVariation.gray}
          actionIdentificationHashes={actionIdentificationHashes}
          getAccountInfo={getAccountInfo}
          shouldCollapse
          {...rest}
        />
      </FlexColumn>
    </MobileFeedItemContainer>
  );
  const responsiveFeedItem = useMatchMedia<any>(
    [onMobile, onAbove, onAbove, onAbove],
    [deploy, accountInfo]
  );

  return <StyledPageTile>{responsiveFeedItem}</StyledPageTile>;
};
